import React, { useState } from 'react';
import {
  X, CheckCircle2, Circle, AlertTriangle, Clock,
  DollarSign, RefreshCw, Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';

// ── Lifecycle helpers (mirrored from OfficerRow — same source of truth) ────────
// checkIn/checkOut live inside offer.shifts[0], NOT at the top level.
const STEPS = [
  { key: 'open',      label: 'Offer Sent',  description: 'Officer has received the offer' },
  { key: 'accepted',  label: 'Accepted',    description: 'Officer accepted the shift'      },
  { key: 'checkin',   label: 'Checked In',  description: 'Officer checked in on site'      },
  { key: 'checkout',  label: 'Checked Out', description: 'Officer checked out'             },
  { key: 'completed', label: 'Completed',   description: 'Shift completed'                 },
  { key: 'paid',      label: 'Paid',        description: 'Payment processed'               },
];

function resolveStepIndex(offer) {
  if (!offer) return 0;
  if (offer.status === 'paid')                          return 5;
  if (offer.status === 'completed')                     return 4;
  if (offer.shifts?.[0]?.checkOut?.confirmed)           return 3;
  if (offer.shifts?.[0]?.checkIn?.confirmed)            return 2;
  if (offer.status === 'accepted' || offer.status === 'active') return 1;
  return 0;
}

const STATUS_CFG = {
  open:      { label: 'Offered',   colors: 'bg-amber-50 text-amber-700 border-amber-200'       },
  accepted:  { label: 'Assigned',  colors: 'bg-sky-50 text-sky-700 border-sky-200'             },
  active:    { label: 'Active',    colors: 'bg-blue-50 text-blue-700 border-blue-200'           },
  completed: { label: 'Completed', colors: 'bg-gray-50 text-gray-600 border-gray-200'           },
  disputed:  { label: 'Disputed',  colors: 'bg-red-50 text-red-600 border-red-200'              },
  resolved:  { label: 'Resolved',  colors: 'bg-emerald-50 text-emerald-700 border-emerald-200'  },
  paid:      { label: 'Paid',      colors: 'bg-emerald-50 text-emerald-700 border-emerald-200'  },
  expired:   { label: 'Expired',   colors: 'bg-gray-50 text-gray-400 border-gray-200'           },
  rejected:  { label: 'Declined',  colors: 'bg-gray-50 text-gray-400 border-gray-200'           },
};

function fmtTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtDateTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

/**
 * OfferPanel — right-side detail panel in the split-pane ShiftDetailMaster layout.
 *
 * Reads entirely from the `offer` prop — no API calls.
 * Dispute resolution is the only action; fires `onResolve(reason)` callback to parent.
 *
 * Props:
 *   offer      — full offer object from ShiftDetailMaster's offers[] array
 *   onClose    — () => void   — collapse panel (parent navigates back to /shift/:shiftId)
 *   onResolve  — (reason: string) => Promise<void>
 *   resolving  — bool (parent controls spinner while the API call runs)
 */
const OfferPanel = ({ offer, onClose, onResolve, resolving }) => {
  const [disputeNote, setDisputeNote] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);

  if (!offer) return null;

  const resource     = offer.provider_resource_id;
  const user         = resource?.user_id || {};
  const name         = user.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : 'Officer';
  const avatar       = user.profilePictureUrl;

  const shiftEmbed   = offer.shifts?.[0];
  const checkedIn    = shiftEmbed?.checkIn?.confirmed;
  const checkedOut   = shiftEmbed?.checkOut?.confirmed;
  const checkInTs    = shiftEmbed?.checkIn?.timestamp;
  const checkOutTs   = shiftEmbed?.checkOut?.timestamp;

  const stepIndex    = resolveStepIndex(offer);
  const isDisputed   = offer.status === 'disputed';
  const isResolved   = offer.status === 'resolved';
  const isPaid       = offer.status === 'paid';
  const isHistorical = ['expired', 'rejected'].includes(offer.status);
  const statusCfg    = STATUS_CFG[offer.status] ?? STATUS_CFG.open;
  const rate         = offer.negotiations?.providerHourlyRate;

  const handleResolveSubmit = async () => {
    if (!disputeNote.trim()) return;
    await onResolve(disputeNote);
    setDisputeNote('');
    setShowResolveForm(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 16 }}
      transition={{ duration: 0.18 }}
      className="rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col h-full"
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2.5 min-w-0">
          {/* Avatar */}
          <div className="h-8 w-8 flex-shrink-0 rounded-full bg-primary/10 overflow-hidden flex items-center justify-center">
            {avatar
              ? <img src={avatar} alt={name} className="h-full w-full object-cover" />
              : <span className="text-[10px] font-bold text-primary">{name.charAt(0).toUpperCase()}</span>
            }
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-semibold text-card-foreground truncate">{name}</p>
              {offer.parentOfferId && (
                <span className="inline-flex items-center gap-0.5 rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[9px] font-medium text-purple-700">
                  <RefreshCw size={8} /> Revised
                </span>
              )}
            </div>
            {user.email && (
              <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusCfg.colors}`}>
            {statusCfg.label}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground hover:bg-muted"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto">

        {/* Rate row */}
        {rate != null && (
          <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border/50 bg-muted/10">
            <DollarSign size={12} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-card-foreground">${rate.toFixed(2)}/hr</span>
            <span className="text-[10px] text-muted-foreground">(officer pay rate)</span>
          </div>
        )}

        {/* ── Lifecycle stepper ── */}
        <div className="px-4 py-4 space-y-1">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Lifecycle</p>

          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-[11px] top-3 bottom-3 w-px bg-border" />

            <div className="space-y-4">
              {STEPS.map((step, i) => {
                const done     = i < stepIndex;
                const active   = i === stepIndex && !isDisputed && !isResolved && !isHistorical;
                const future   = i > stepIndex;

                return (
                  <motion.div
                    key={step.key}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-start gap-3 relative"
                  >
                    <div className={`relative z-10 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                      done
                        ? 'border-emerald-500 bg-emerald-500'
                        : active
                          ? 'border-brand-pink bg-white ring-2 ring-brand-pink/20'
                          : 'border-border bg-background'
                    }`}>
                      {done
                        ? <CheckCircle2 size={12} className="text-white" />
                        : active
                          ? <div className="h-2 w-2 rounded-full bg-brand-pink animate-pulse" />
                          : <Circle size={10} className="text-muted-foreground/30" />
                      }
                    </div>
                    <div className={`pt-0.5 ${future && !isResolved ? 'opacity-40' : ''}`}>
                      <p className={`text-xs font-semibold ${active ? 'text-brand-pink' : done ? 'text-card-foreground' : 'text-muted-foreground'}`}>
                        {step.label}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{step.description}</p>

                      {/* Check-in timestamp inline */}
                      {step.key === 'checkin' && checkedIn && checkInTs && (
                        <p className="mt-0.5 text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                          <Clock size={9} /> {fmtDateTime(checkInTs)}
                          {shiftEmbed?.checkIn?.timeWindow?.start && (
                            <span className="text-muted-foreground font-normal">
                              (window {fmtTime(shiftEmbed.checkIn.timeWindow.start)}–{fmtTime(shiftEmbed.checkIn.timeWindow.end)})
                            </span>
                          )}
                        </p>
                      )}
                      {/* Check-out timestamp inline */}
                      {step.key === 'checkout' && checkedOut && checkOutTs && (
                        <p className="mt-0.5 text-[10px] text-brand-blue-dark font-medium flex items-center gap-1">
                          <Clock size={9} /> {fmtDateTime(checkOutTs)}
                          {shiftEmbed?.checkOut?.timeWindow?.start && (
                            <span className="text-muted-foreground font-normal">
                              (window {fmtTime(shiftEmbed.checkOut.timeWindow.start)}–{fmtTime(shiftEmbed.checkOut.timeWindow.end)})
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {/* Disputed / Resolved node */}
              {(isDisputed || isResolved) && (
                <motion.div
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-3 relative"
                >
                  <div className={`relative z-10 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full border-2 ${
                    isResolved ? 'border-emerald-500 bg-emerald-500' : 'border-amber-400 bg-amber-50 ring-2 ring-amber-100'
                  }`}>
                    {isResolved
                      ? <CheckCircle2 size={12} className="text-white" />
                      : <AlertTriangle size={10} className="text-amber-600" />
                    }
                  </div>
                  <div className="pt-0.5">
                    <p className={`text-xs font-semibold ${isResolved ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {isResolved ? 'Resolved' : 'Disputed'}
                    </p>
                    {offer.dispute?.definition && (
                      <p className="text-[10px] text-muted-foreground mt-0.5 italic">"{offer.dispute.definition}"</p>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* ── Expiry warning ── */}
        {offer.status === 'open' && offer.negotiations?.expiredAt && (
          <div className="mx-4 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <p className="text-[10px] text-amber-700">
              ⏱ Offer expires {fmtDateTime(offer.negotiations.expiredAt)}
            </p>
          </div>
        )}

        {/* ── Dispute resolution form ── */}
        {isDisputed && !isResolved && (
          <div className="mx-4 mb-4 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-amber-200">
              <div className="flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-amber-600" />
                <p className="text-xs font-semibold text-amber-700">Dispute pending resolution</p>
              </div>
              {!showResolveForm && (
                <button
                  type="button"
                  onClick={() => setShowResolveForm(true)}
                  className="text-[11px] font-medium text-amber-700 hover:underline underline-offset-2"
                >
                  Resolve →
                </button>
              )}
            </div>

            {showResolveForm && (
              <div className="p-3 space-y-2">
                <textarea
                  value={disputeNote}
                  onChange={(e) => setDisputeNote(e.target.value)}
                  placeholder="Enter resolution notes…"
                  rows={3}
                  className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-amber-400/30 resize-none"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowResolveForm(false); setDisputeNote(''); }}
                    disabled={resolving}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleResolveSubmit}
                    disabled={resolving || !disputeNote.trim()}
                    className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-600 disabled:opacity-50"
                  >
                    {resolving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                    Confirm Resolution
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Resolved note */}
        {isResolved && (
          <div className="mx-4 mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
            <p className="text-[10px] text-emerald-700 font-medium">✓ Dispute resolved</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default OfferPanel;
